/**
 * @author      Shivani Mehra
 * @date        Oct, 2025
 * @copyright   www.ibirdsservices.com
 */

import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Container,
  Row,
  Table,
  Card,
  Form,
  Alert,
  Modal,
} from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  DatatableWrapper,
  Filter,
  Pagination,
  PaginationOptions,
  TableBody,
  TableHeader,
} from 'react-bs-datatable';
import { useNavigate, Link } from 'react-router-dom';
import Confirm from '../../Confirm';
import jwt_decode from 'jwt-decode';
import helper from '../../common/helper';
import * as constants from '../../../constants/CONSTANT';
import { FaTrash, FaEdit, FaPlus } from 'react-icons/fa';
import { ShimmerTable } from 'react-shimmer-effects';
import WatconnectApi from '../../../api/WatconnectApi';
import DataApi from '../../../api/DataApi';

const Templates = ({ socket, selectedWhatsAppSetting }) => {
  const navigate = useNavigate();
  const [category, setCategory] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [body, setBody] = useState([]);
  const [templateDelete, setTemplateDelete] = useState(false);
  const [rowData, setRowData] = useState();
  const [isSpinner, setIsSpinner] = useState(false);
  const [userInfo, setUserInfo] = useState({});
  const categories = ['AUTHENTICATION', 'UTILITY', 'MARKETING'];
  const [alerts, setAlerts] = useState([]);
  const watconnectApi = new WatconnectApi('webhook_template');
  const dataApi = new DataApi('watconnect');

  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const handlePreviewClick = template => {
    setPreviewTemplate(template);
    setShowPreviewModal(true);
  };

  useEffect(() => {
    if (!socket) {
      console.log('⚠️ No socket found in Templates component');
      return;
    }
    console.log('✅ Socket available in Templates:', socket.id);

    const showAlert = (type, data) => {
      console.log(`📩 Received ${type} update:`, data);

      const newAlert = { id: Date.now(), type, data };
      setAlerts(prev => [...prev, newAlert]);

      setTimeout(() => {
        setAlerts(prev => prev.filter(a => a.id !== newAlert.id));
      }, 300000);
    };

    const handleStatusUpdate = async data => {
      try {
        if (
          data.business_number &&
          data.business_number !== selectedWhatsAppSetting
        ) {
          console.log(
            'Skipping status update for different business number:',
            data.business_number
          );
          return;
        }

        showAlert('status', data);

        const {
          message_template_id,
          message_template_name,
          message_template_language,
          status,
        } = data;

        await dataApi.post(`/template`, {
          template_id: message_template_id,
          template_name: message_template_name,
          language: message_template_language,
          status,
          primaryKeys: ['template_name'],
        });

        fetchAllTemplates(selectedWhatsAppSetting);
      } catch (error) {
        console.error('❌ Error handling status update:', error);
      }
    };

    const handleCategoryUpdate = async data => {
      try {
        if (
          data.business_number &&
          data.business_number !== selectedWhatsAppSetting
        ) {
          console.log(
            'Skipping category update for different business number:',
            data.business_number
          );
          return;
        }

        showAlert('category', data);

        const {
          message_template_id,
          message_template_name,
          message_template_language,
          new_category,
        } = data;

        await dataApi.post(`/template`, {
          template_id: message_template_id,
          template_name: message_template_name,
          language: message_template_language,
          category: new_category,
          primaryKeys: ['template_name'],
        });

        fetchAllTemplates(selectedWhatsAppSetting);
      } catch (error) {
        console.error('❌ Error handling category update:', error);
      }
    };

    socket.on('template_status_update', handleStatusUpdate);
    socket.on('template_category_update', handleCategoryUpdate);

    return () => {
      socket.off('template_status_update', handleStatusUpdate);
      socket.off('template_category_update', handleCategoryUpdate);
    };
  }, [socket, selectedWhatsAppSetting]);

  useEffect(() => {
    if (selectedWhatsAppSetting) {
      fetchAllTemplates(selectedWhatsAppSetting);
    } else {
      setIsSpinner(true);
    }
  }, [selectedWhatsAppSetting]);

  const onFilterType = event => {
    const selectedCategory = event.target.value;
    setCategory(selectedCategory);

    let filtered = body;

    if (selectedCategory) {
      filtered = filtered.filter(
        data => data.category.toLowerCase() === selectedCategory.toLowerCase()
      );
    }

    if (searchQuery) {
      filtered = filtered.filter(
        data =>
          data.templatename.toLowerCase().includes(searchQuery.toLowerCase()) ||
          data.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
          data.language.toLowerCase().includes(searchQuery.toLowerCase()) ||
          data.status.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredData(filtered);
  };

  // Add a search query state
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = event => {
    setSearchQuery(event.target.value);
    onFilterType({ target: { value: category } });
  };
  const fetchAllTemplates = async selectedWhatsAppSetting => {
    let user = jwt_decode(localStorage.getItem('watconnect_token'));
    setUserInfo(user);

    const result = await dataApi.fetchById(
      `watconnect_template?business_number=${selectedWhatsAppSetting}`
    );

    if (result.error || result.success === false) {
      toast.error(result?.error?.message);
      setBody([]);
      setFilteredData([]);
      setIsSpinner(true);
      return;
    }

    if (result?.data?.data && result?.data?.data.length > 0) {
      // ✅ Filter out pending templates
      const validTemplates = result.data.data.filter(
        t =>
          t.status &&
          t.status.toUpperCase() !== 'PENDING_DELETION' &&
          t.status.toUpperCase() !== 'DELETED'
      );

      const transformedData = validTemplates.map(row => {
        // Handle carousel templates
        if (row.template_type === 'carousel' && Array.isArray(row.cards)) {
          const cards = row.cards.map(card => {
            const header = card.header || {};
            const body = card.body || {};
            const buttons = card.buttons || [];

            return {
              header_type: header.format || '',
              header_text: header.format === 'TEXT' ? header.text : '',
              header_image_url: header.format === 'IMAGE' ? header.example : '',
              header_document_url:
                header.format === 'DOCUMENT' ? header.example : '',
              header_video_url: header.format === 'VIDEO' ? header.example : '',
              body_text: body.text || '',
              body_text_example: body.example || [],
              buttons: buttons.map(btn => ({
                type: btn.type,
                text: btn.text,
                ...(btn.type === 'PHONE_NUMBER' && {
                  phone_number: btn.phone_number,
                }),
                ...(btn.type === 'URL' && { url: btn.url }),
              })),
            };
          });

          return {
            id: row.id,
            name: row.template_name,
            templateid: row.template_id,
            templatename: row.template_name,
            language: row.language,
            status: row.status || 'PENDING',
            category: row.category,
            template_type: 'carousel',
            message_body: row.message_body || '',
            footer: row.footer || '',
            cards,
            lastmodifieddate: row.lastmodifieddate,
            ownerid: row.ownerid || row.createdbyid,
            createdbyid: row.createdbyid || row.ownerid,
            owner_name: row.owner_name || row.createdbyname || row.created_by_name || 'System',
            createdbyname: row.createdbyname || row.owner_name || row.created_by_name || 'System',
          };
        }

        // Handle normal templates (with file attachments)
        const rawFileUrl = row?.file_data?.file_url
          ? row.file_data.file_url.replace(/\\/g, '/')
          : (row?.header_body && row.header_body !== 'null' && (row.header_body.startsWith('http') || row.header_body.startsWith('/')))
            ? row.header_body
            : null;

        const formatFullUrl = url => {
          if (!url) return '';
          if (url.startsWith('http://') || url.startsWith('https://')) return url;
          if (url.startsWith('/')) {
            const apiBase = constants.API_BASE_URL ? constants.API_BASE_URL.replace(/\/+$/, '') : '';
            return `${apiBase}${url}`;
          }
          return url;
        };

        const fileUrl = formatFullUrl(rawFileUrl);
        const mimeType = row?.file_data?.mime_type || '';
        let header_image_url = '';
        let header_video_url = '';
        let header_document_url = '';

        if (row.header === 'IMAGE') header_image_url = fileUrl || '';
        else if (row.header === 'VIDEO') header_video_url = fileUrl || '';
        else if (row.header === 'DOCUMENT') header_document_url = fileUrl || '';

        return {
          id: row.id,
          name: row.template_name,
          templateid: row.template_id,
          templatename: row.template_name,
          language: row.language,
          status: row.status || 'PENDING',
          category: row.category,
          header: row.header !== 'null' ? row.header : '',
          header_text: row.header === 'TEXT' ? row.header_body : '',
          header_image_url: row.header === 'IMAGE' ? header_image_url : '',
          header_video_url: row.header === 'VIDEO' ? header_video_url : '',
          header_document_url:
            row.header === 'DOCUMENT' ? header_document_url : '',
          message_body: row.message_body || '',
          footer: row.footer !== 'null' ? row.footer : '',
          buttons: Array.isArray(row.buttons)
            ? row.buttons.map(btn => ({
              type: btn.type,
              text: btn.text,
              ...(btn.type === 'PHONE_NUMBER' && {
                phone_number: btn.phone_number,
              }),
              ...(btn.type === 'URL' && { url: btn.url }),
            }))
            : [],
          example_body_text: row.example_body_text || null,
          lastmodifieddate: row.lastmodifieddate,
          ownerid: row.ownerid || row.createdbyid,
          createdbyid: row.createdbyid || row.ownerid,
          owner_name: row.owner_name || row.createdbyname || row.created_by_name || 'System',
          createdbyname: row.createdbyname || row.owner_name || row.created_by_name || 'System',
          file_data: row.file_data || (fileUrl ? { file_url: fileUrl, mime_type: mimeType } : null),
        };
      });

      // ✅ Sort templates by latest modified date
      const sortedData = transformedData.sort(
        (a, b) => new Date(b.lastmodifieddate) - new Date(a.lastmodifieddate)
      );

      setBody(sortedData);
      setFilteredData(sortedData);
    } else {
      setBody([]);
      setFilteredData([]);
    }

    setIsSpinner(true);
  };

  const labels = { beforeSelect: ' ' };
  const header = [
    {
      title: 'Name',
      prop: 'templatename',
      isFilterable: true,
      cell: row => {
        const formatName = name => {
          if (!name) return '';
          return name
            .toLowerCase()
            .replace(/_/g, ' ')
            .replace(/\b\w/g, char => char.toUpperCase());
        };

        const displayName = formatName(row.templatename);

        return (
          <span
            onClick={() => handlePreviewClick(row)}
            style={{
              color: '#007bff',
              cursor: 'pointer',
              fontWeight: 500,
              textDecoration: 'none',
            }}
            onMouseEnter={e =>
              (e.currentTarget.style.textDecoration = 'underline')
            }
            onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
          >
            {displayName}
          </span>
        );
      },
    },
    { title: 'category', prop: 'category', isFilterable: true },
    { title: 'language', prop: 'language', isFilterable: true },
    {
      title: 'Status',
      prop: 'status',
      isFilterable: true,
      cell: row => {
        return (
          <>
            {row.status === 'APPROVED' ? (
              <Button
                className='btn-sm'
                variant=''
                style={{
                  borderRadius: '7px',
                  backgroundColor: 'rgb(154 235 233)',
                  cursor: 'default',
                }}
              >
                {row.status}
              </Button>
            ) : row.status === 'PENDING' ? (
              <Button
                className='btn-sm'
                variant=''
                style={{
                  borderRadius: '15px',
                  backgroundColor: 'rgb(241 218 177)',
                  cursor: 'default',
                }}
              >
                {row.status}
              </Button>
            ) : row.status === 'REJECTED' ? (
              <Button
                className='btn-sm'
                variant=''
                style={{
                  borderRadius: '15px',
                  backgroundColor: 'rgb(248 195 200)',
                  cursor: 'default',
                }}
              >
                {row.status}
              </Button>
            ) : null}
          </>
        );
      },
    },
    {
      title: 'Created At',
      prop: 'lastmodifieddate',
      isFilterable: false,
      cell: row => {
        const dateToShow = row.lastmodifieddate;
        if (!dateToShow) return '-';

        const formattedDate = new Date(dateToShow).toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });

        return (
          <span
            className='text-dark px-2 py-1'
            style={{
              borderRadius: '10px',
              fontSize: '12px',
              fontWeight: 500,
            }}
          >
            {formattedDate}
          </span>
        );
      },
    },
    {
      title: 'Owner Name',
      prop: 'owner_name',
      isFilterable: true,
      cell: row => {
        const ownerName = row.owner_name || row.createdbyname || row.created_by_name || 'System';
        const ownerId = row.ownerid || row.createdbyid;
        if (ownerId && ownerName !== 'System') {
          return (
            <Link to={`/view/user/${ownerId}`} state={row}>
              {ownerName}
            </Link>
          );
        }
        return ownerName;
      },
    },
    {
      title: 'Actions',
      prop: 'id',
      cell: row => (
        <>
          <Button
            className='btn-sm mx-2'
            variant='danger'
            onClick={() => removeSelectedRow({ row })}
            title='Delete'
          >
            <i className='fa-regular fa-trash-can'></i>
          </Button>
          <Button
            className='btn-sm mx-2'
            variant='primary'
            onClick={() => cloneRecord({ row })}
            title='Clone'
            style={{ borderRadius: '6px' }}
          >
            <i className='fa-solid fa-clone'></i> Clone
          </Button>
        </>
      ),
    },
  ].filter(Boolean);

  const cloneRecord = data => {
    console.log('data', data);
    navigate(`/template/add`, {
      state: { ...data?.row, type: 'clone' },
    });
  };

  const addNewTemplate = () => {
    navigate(`/template/add`);
  };

  const editTemplate = data => {
    navigate(`/template/add`, { state: data.row });
  };

  const removeSelectedRow = data => {
    setTemplateDelete(true);
    setRowData(data.row);
  };

  const deleteTemplate = async () => {
    if (rowData.templateid && rowData.templatename) {
      const result = await watconnectApi.deleteUrl(
        `/template?hsm_id=${rowData.templateid}&name=${rowData.templatename}&whatsapp_setting_number=${selectedWhatsAppSetting}`
      );
      if (result.success) {
        setTemplateDelete(false);
        toast.success('Template deleted successfully.');
        fetchAllTemplates(selectedWhatsAppSetting);
      } else {
        setTemplateDelete(false);
        toast.error(result?.error?.message || 'Failed to delete template.');
      }
    }
  };

  return (
    <>
      <Container className='mt-5'>
        <Row className='mx-5 mb-4'>
          <Card className='p-4 bg-white shadow-sm border-0 rounded-3'>
            <h5 className='mb-2 fw-bold'>💡 Quick Guide</h5>
            <small>
              WhatsApp templates allow businesses to send structured,
              pre-approved messages such as alerts, updates, and reminders. To
              ensure successful delivery, templates must comply with Meta's
              policies and follow the approved formatting rules.
              <br></br>
              👉 For detailed guidance, check the official{' '}
              <a
                target='_blank'
                href='https://developers.facebook.com/docs/whatsapp/updates-to-pricing/new-template-guidelines/'
                rel='noreferrer'
              >
                Meta Template Guidelines
              </a>
            </small>
          </Card>
        </Row>
        <Row className='mx-5 mb-4'>
          {alerts?.map(alert => (
            <Alert
              key={alert?.id}
              variant={
                alert?.type === 'status'
                  ? alert?.data?.status === 'APPROVED'
                    ? 'success'
                    : 'warning'
                  : 'info'
              }
              dismissible
              onClose={() =>
                setAlerts(prev => prev.filter(a => a?.id !== alert?.id))
              }
            >
              {alert?.type === 'status' ? (
                <>
                  <strong>Template {alert?.data?.status}!</strong> <br />
                  {alert?.data?.message_template_name} (
                  {alert?.data?.message_template_language})
                  {alert?.data?.reason && <> – Reason: {alert?.data?.reason}</>}
                </>
              ) : (
                <>
                  <strong>Category Updated!</strong> <br />
                  {alert?.data?.message_template_name} moved to{' '}
                  <b>{alert?.data?.new_category}</b>
                </>
              )}
            </Alert>
          ))}
        </Row>
        <Row className='mx-5 g-0'>
          <Col lg={12} className='mb-3'>
            <Row className='g-0'>
              <Col lg={12}>
                <div className='bg-white shadow-sm border-0 rounded-3 p-4'>
                  <h5 className='fw-semibold mb-1'>📦 All Templates</h5>

                  {isSpinner ? (
                    <DatatableWrapper
                      body={body}
                      headers={header}
                      paginationOptionsProps={{
                        initialState: {
                          rowsPerPage: 10,
                          options: [5, 10, 15, 20],
                        },
                      }}
                    >
                      <Row className='mb-3'>
                        <Col
                          lg={3}
                          sm={10}
                          xs={10}
                          className='d-flex flex-col justify-content-end align-items-end'
                        >
                          <Filter />
                        </Col>
                        <Col
                          lg={4}
                          sm={2}
                          xs={2}
                          className='d-flex flex-col justify-content-start align-items-start'
                        >
                          <PaginationOptions labels={labels} />
                        </Col>
                        <Col
                          lg={5}
                          sm={12}
                          xs={12}
                          className='mt-2 d-flex flex-col justify-content-end align-items-end'
                        >
                          <Button
                            variant='primary'
                            className='new-list-btn'
                            onClick={addNewTemplate}
                          >
                            <FaPlus /> Add New Template
                          </Button>
                        </Col>
                      </Row>

                      <div className='table-responsive'>
                        <Table
                          striped
                          bordered
                          className='related-list-table'
                          responsive='sm'
                        >
                          <TableHeader />
                          <TableBody />
                        </Table>
                      </div>
                      <Pagination />
                    </DatatableWrapper>
                  ) : (
                    <ShimmerTable row={10} col={4} />
                  )}
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
      {templateDelete && (
        <Confirm
          show={templateDelete}
          onHide={() => setTemplateDelete(false)}
          deleteTemplate={deleteTemplate}
          title='Confirm Delete'
          message='You are going to delete the record. Are you sure?'
          table='template'
        />
      )}

      {/* WhatsApp Template Preview Modal */}
      {showPreviewModal && previewTemplate && (
        <Modal
          show={showPreviewModal}
          onHide={() => setShowPreviewModal(false)}
          centered
          size='md'
          className='wa-preview-modal'
          dialogClassName='wa-preview-dialog'
        >
          <Modal.Header
            closeButton
            style={{ borderBottom: 'none', background: '#f0f2f5' }}
          >
            <Modal.Title
              style={{ fontSize: '18px', fontWeight: 600, color: '#3b3b3b' }}
            >
              Template Preview:{' '}
              {previewTemplate.templatename
                ? previewTemplate.templatename
                  .toLowerCase()
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, char => char.toUpperCase())
                : ''}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body
            style={{
              background:
                '#ece5dd url(https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png)',
              backgroundSize: 'contain',
              padding: '24px',
              minHeight: '400px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            {previewTemplate.template_type === 'carousel' &&
              Array.isArray(previewTemplate.cards) ? (
              /* Carousel Preview Scroll Container */
              <div
                style={{
                  display: 'flex',
                  overflowX: 'auto',
                  gap: '16px',
                  paddingBottom: '16px',
                  scrollbarWidth: 'thin',
                }}
              >
                {previewTemplate.cards.map((card, cardIndex) => {
                  const cardExamples = card.body_text_example || [];

                  // Format card body text with variables replaced by examples
                  const formatCardBody = () => {
                    const text = card.body_text || '';
                    const parts = text.split(/(\{\{\d+\}\})/g);
                    return parts.map((part, i) => {
                      const match = part.match(/\{\{(\d+)\}\}/);
                      if (match) {
                        const number = match[1];
                        const idx = parseInt(number) - 1;
                        const value =
                          cardExamples[idx] !== undefined
                            ? cardExamples[idx]
                            : `{{${number}}}`;
                        return (
                          <strong
                            key={i}
                            style={{
                              color: '#25D366',
                              background: 'rgba(37, 211, 102, 0.1)',
                              padding: '1px 4px',
                              borderRadius: '4px',
                              margin: '0 2px',
                              fontSize: '13px',
                            }}
                          >
                            {value}
                          </strong>
                        );
                      }
                      return part;
                    });
                  };

                  return (
                    <div
                      key={cardIndex}
                      style={{
                        minWidth: '270px',
                        maxWidth: '270px',
                        background: '#ffffff',
                        borderRadius: '7.5px',
                        boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        border: '1px solid #e1e9eb',
                      }}
                    >
                      {/* Card Header Media */}
                      {card.header_type === 'IMAGE' &&
                        card.header_image_url && (
                          <img
                            src={card.header_image_url}
                            alt='Header'
                            style={{
                              width: '100%',
                              height: '140px',
                              objectFit: 'cover',
                            }}
                          />
                        )}
                      {card.header_type === 'VIDEO' &&
                        card.header_video_url && (
                          <video
                            src={card.header_video_url}
                            controls
                            style={{
                              width: '100%',
                              height: '140px',
                              objectFit: 'cover',
                            }}
                          />
                        )}
                      {card.header_type === 'DOCUMENT' && (
                        <div
                          style={{
                            height: '140px',
                            background: '#f0f2f5',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#667781',
                            cursor: card.header_document_url ? 'pointer' : 'default',
                          }}
                          onClick={() => {
                            if (card.header_document_url) {
                              window.open(card.header_document_url, '_blank');
                            }
                          }}
                        >
                          <i
                            className='fa-solid fa-file-pdf'
                            style={{ fontSize: '48px', color: '#ea4335' }}
                          ></i>
                          <small
                            style={{ marginTop: '8px', fontWeight: 500 }}
                          >
                            {card.header_document_url
                              ? 'View Document'
                              : 'Document Attached'}
                          </small>
                        </div>
                      )}
                      {card.header_type === 'TEXT' && card.header_text && (
                        <div
                          style={{
                            padding: '12px 12px 0 12px',
                            fontWeight: 'bold',
                            color: '#111b21',
                            fontSize: '14px',
                          }}
                        >
                          {card.header_text}
                        </div>
                      )}

                      {/* Card Body */}
                      <div
                        style={{
                          padding: '12px',
                          fontSize: '13.5px',
                          color: '#111b21',
                          lineHeight: '1.4',
                          flexGrow: 1,
                        }}
                      >
                        {formatCardBody()}
                      </div>

                      {/* Card Buttons */}
                      {card.buttons && card.buttons.length > 0 && (
                        <div
                          style={{
                            borderTop: '1px solid #f0f2f5',
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          {card.buttons.map((btn, btnIdx) => (
                            <div
                              key={btnIdx}
                              style={{
                                padding: '10px',
                                textAlign: 'center',
                                color: '#008069',
                                fontWeight: '500',
                                fontSize: '13px',
                                borderBottom:
                                  btnIdx < card.buttons.length - 1
                                    ? '1px solid #f0f2f5'
                                    : 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '6px',
                              }}
                            >
                              {btn.type === 'URL' && (
                                <i
                                  className='fa-solid fa-arrow-up-right-from-square'
                                  style={{ fontSize: '11px' }}
                                ></i>
                              )}
                              {btn.type === 'PHONE_NUMBER' && (
                                <i
                                  className='fa-solid fa-phone'
                                  style={{ fontSize: '11px' }}
                                ></i>
                              )}
                              {btn.type === 'QUICK_REPLY' && (
                                <i
                                  className='fa-solid fa-reply'
                                  style={{ fontSize: '11px' }}
                                ></i>
                              )}
                              {btn.text}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Normal Bubble Preview */
              <div
                style={{
                  background: '#ffffff',
                  borderRadius: '7.5px',
                  boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
                  padding: '8px 10px',
                  maxWidth: '90%',
                  margin: '0 auto',
                  minWidth: '280px',
                  position: 'relative',
                  border: '1px solid #e1e9eb',
                }}
              >
                {/* Header Media */}
                {previewTemplate.header === 'IMAGE' && (
                  previewTemplate.header_image_url ? (
                    <img
                      src={previewTemplate.header_image_url}
                      alt='Header'
                      style={{
                        width: '100%',
                        borderRadius: '6px',
                        maxHeight: '180px',
                        objectFit: 'cover',
                        marginBottom: '8px',
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        background: '#f0f2f5',
                        borderRadius: '6px',
                        height: '120px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#667781',
                        marginBottom: '8px',
                        border: '1px solid #e1e9eb',
                      }}
                    >
                      <i className='fa-solid fa-image' style={{ fontSize: '36px', color: '#54656f' }}></i>
                      <small style={{ marginTop: '6px', fontWeight: 500 }}>Image Attachment</small>
                    </div>
                  )
                )}
                {previewTemplate.header === 'VIDEO' && (
                  previewTemplate.header_video_url ? (
                    <video
                      src={previewTemplate.header_video_url}
                      controls
                      style={{
                        width: '100%',
                        borderRadius: '6px',
                        maxHeight: '180px',
                        objectFit: 'cover',
                        marginBottom: '8px',
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        background: '#f0f2f5',
                        borderRadius: '6px',
                        height: '120px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#667781',
                        marginBottom: '8px',
                        border: '1px solid #e1e9eb',
                      }}
                    >
                      <i className='fa-solid fa-video' style={{ fontSize: '36px', color: '#54656f' }}></i>
                      <small style={{ marginTop: '6px', fontWeight: 500 }}>Video Attachment</small>
                    </div>
                  )
                )}
                {previewTemplate.header === 'DOCUMENT' && (
                  <div
                    style={{
                      background: '#f0f2f5',
                      borderRadius: '6px',
                      padding: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      color: '#111b21',
                      marginBottom: '8px',
                      border: '1px solid #e1e9eb',
                      cursor: previewTemplate.header_document_url ? 'pointer' : 'default',
                    }}
                    onClick={() => {
                      if (previewTemplate.header_document_url) {
                        window.open(previewTemplate.header_document_url, '_blank');
                      }
                    }}
                  >
                    <i
                      className='fa-solid fa-file-pdf'
                      style={{ fontSize: '32px', color: '#ea4335' }}
                    ></i>
                    <div style={{ overflow: 'hidden' }}>
                      <div
                        style={{
                          fontWeight: '500',
                          fontSize: '13px',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {previewTemplate.header_document_url
                          ? previewTemplate.header_document_url.split('/').pop() || 'Document.pdf'
                          : 'Document.pdf'}
                      </div>
                      <small style={{ color: '#667781', fontSize: '11px' }}>
                        {previewTemplate.header_document_url
                          ? 'Click to view document'
                          : 'Pre-approved attachment'}
                      </small>
                    </div>
                  </div>
                )}
                {previewTemplate.header === 'TEXT' &&
                  previewTemplate.header_text && (
                    <div
                      style={{
                        fontWeight: 'bold',
                        color: '#111b21',
                        fontSize: '14.5px',
                        marginBottom: '6px',
                      }}
                    >
                      {previewTemplate.header_text}
                    </div>
                  )}

                {/* Message Body */}
                <div
                  style={{
                    fontSize: '14px',
                    lineHeight: '1.4',
                    color: '#111b21',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {(() => {
                    const text = previewTemplate.message_body || '';
                    let examples = [];
                    if (previewTemplate.example_body_text) {
                      try {
                        const parsed =
                          typeof previewTemplate.example_body_text === 'string'
                            ? JSON.parse(previewTemplate.example_body_text)
                            : previewTemplate.example_body_text;
                        if (Array.isArray(parsed) && parsed.length > 0) {
                          examples = Array.isArray(parsed[0])
                            ? parsed[0]
                            : parsed;
                        }
                      } catch (e) {
                        console.error('Error parsing example_body_text', e);
                      }
                    }
                    const parts = text.split(/(\{\{\d+\}\})/g);
                    return parts.map((part, i) => {
                      const match = part.match(/\{\{(\d+)\}\}/);
                      if (match) {
                        const number = match[1];
                        const idx = parseInt(number) - 1;
                        const value =
                          examples[idx] !== undefined
                            ? examples[idx]
                            : `{{${number}}}`;
                        return (
                          <strong
                            key={i}
                            style={{
                              color: '#25D366',
                              background: 'rgba(37, 211, 102, 0.1)',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              margin: '0 2px',
                            }}
                          >
                            {value}
                          </strong>
                        );
                      }
                      return part;
                    });
                  })()}
                </div>

                {/* Footer */}
                {previewTemplate.footer && (
                  <div
                    style={{
                      fontSize: '11px',
                      color: '#667781',
                      marginTop: '6px',
                      textAlign: 'left',
                    }}
                  >
                    {previewTemplate.footer}
                  </div>
                )}

                {/* Interactive Buttons */}
                {previewTemplate.buttons &&
                  previewTemplate.buttons.length > 0 && (
                    <div
                      style={{
                        borderTop: '1px solid rgba(17, 27, 33, 0.06)',
                        marginTop: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      {previewTemplate.buttons.map((btn, btnIdx) => (
                        <div
                          key={btnIdx}
                          style={{
                            padding: '10px 0',
                            textAlign: 'center',
                            color: '#008069',
                            fontWeight: '500',
                            fontSize: '13.5px',
                            borderBottom:
                              btnIdx < previewTemplate.buttons.length - 1
                                ? '1px solid rgba(17, 27, 33, 0.06)'
                                : 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          {btn.type === 'URL' && (
                            <i
                              className='fa-solid fa-arrow-up-right-from-square'
                              style={{ fontSize: '11px' }}
                            ></i>
                          )}
                          {btn.type === 'PHONE_NUMBER' && (
                            <i
                              className='fa-solid fa-phone'
                              style={{ fontSize: '11px' }}
                            ></i>
                          )}
                          {btn.type === 'QUICK_REPLY' && (
                            <i
                              className='fa-solid fa-reply'
                              style={{ fontSize: '11px' }}
                            ></i>
                          )}
                          {btn.text}
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer
            style={{
              background: '#f0f2f5',
              borderTop: 'none',
              display: 'flex',
              justifyContent: 'space-between',
              padding: '12px 24px',
            }}
          >
            <div style={{ fontSize: '12px', color: '#667781' }}>
              <strong>Category:</strong> {previewTemplate.category} |{' '}
              <strong>Language:</strong> {previewTemplate.language}
            </div>
            <Button
              variant='secondary'
              size='sm'
              onClick={() => setShowPreviewModal(false)}
              style={{ borderRadius: '4px' }}
            >
              Close Preview
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      <ToastContainer />
    </>
  );
};

export default Templates;
